import axios from 'axios';
import { callApi } from './api';
import MeditationEvent, { EventPromoCode } from '@/models/meditation-event';
import EventRegistration, { EventRegistrationStatus, EventPaymentStatus } from '@/models/event-registration';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Admin surfaces — all under `/events/admin/*` (admin-role gated).
const eventsAdminApiUrl = import.meta.env.VITE_API_URL_V1 + '/events/admin';

/* ─────────────────────────────── events ─────────────────────────────── */

export const getEvents = async () => {
    return callApi<MeditationEvent[]>({ url: `${eventsAdminApiUrl}/events`, method: 'GET' });
};

export const useEvents = () => {
    return useQuery({ queryKey: ['events'], queryFn: getEvents });
};

export const getEvent = async (eventId: string) => {
    return callApi<MeditationEvent>({ url: `${eventsAdminApiUrl}/events/${eventId}`, method: 'GET' });
};

export const useEvent = (eventId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['event', eventId],
        queryFn: () => getEvent(eventId),
        enabled: !!eventId && isOpen,
    });
};

/**
 * Only editable fields go over the wire (the API's validate() rejects unmatched
 * body keys). `registeredCount` is server-maintained and never sent.
 */
export type EventMutationPayload = {
    slug: string;
    title: string;
    description: string;
    startsAt: string;
    // `null` clears a previously-set end time (undefined would be dropped by axios and
    // leave the old value); omitted/undefined on create means "no end time".
    endsAt?: string | null;
    timezone: string;
    location: string;
    capacity: number;
    active: boolean;
    orderIndex: number;
    /** Registration fee in Toman. `null`/0 clears it (→ a free event). */
    price?: number | null;
    /** Display-only discount codes. An empty array clears them. */
    promoCodes?: EventPromoCode[];
};

export const createEvent = async (payload: EventMutationPayload) => {
    return callApi<MeditationEvent>({ url: `${eventsAdminApiUrl}/events`, method: 'POST', data: payload });
};

export const useCreateEvent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
};

export const updateEvent = async ({ eventId, ...payload }: EventMutationPayload & { eventId: string }) => {
    return callApi<MeditationEvent>({ url: `${eventsAdminApiUrl}/events/${eventId}`, method: 'PUT', data: payload });
};

export const useUpdateEvent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateEvent,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] });
        },
    });
};

export const deleteEvent = async ({ eventId }: { eventId: string }) => {
    return callApi({ url: `${eventsAdminApiUrl}/events/${eventId}`, method: 'DELETE' });
};

export const useDeleteEvent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
};

/* ─────────────────────────── registrations ─────────────────────────── */

export interface RegistrationsListResult {
    registrations: EventRegistration[];
    total: number;
    page: number;
    limit: number;
}

export interface RegistrationsQuery {
    page: number;
    limit: number;
    status?: EventRegistrationStatus;
    eventId?: string;
}

export const getRegistrations = async (params: RegistrationsQuery) => {
    const q = new URLSearchParams();
    q.set('page', String(params.page));
    q.set('limit', String(params.limit));
    if (params.status) q.set('status', params.status);
    if (params.eventId) q.set('eventId', params.eventId);
    return callApi<RegistrationsListResult>({ url: `${eventsAdminApiUrl}/registrations?${q.toString()}`, method: 'GET' });
};

export const useRegistrations = (params: RegistrationsQuery) => {
    return useQuery({ queryKey: ['event-registrations', params], queryFn: () => getRegistrations(params) });
};

export const getRegistration = async (registrationId: string) => {
    return callApi<EventRegistration>({ url: `${eventsAdminApiUrl}/registrations/${registrationId}`, method: 'GET' });
};

export const useRegistration = (registrationId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['event-registration', registrationId],
        queryFn: () => getRegistration(registrationId),
        enabled: !!registrationId && isOpen,
    });
};

export type RegistrationUpdatePayload = {
    status?: EventRegistrationStatus;
    paymentStatus?: EventPaymentStatus;
    adminNote?: string;
};

export const updateRegistration = async ({ registrationId, ...payload }: RegistrationUpdatePayload & { registrationId: string }) => {
    return callApi<EventRegistration>({ url: `${eventsAdminApiUrl}/registrations/${registrationId}`, method: 'PUT', data: payload });
};

export const useUpdateRegistration = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateRegistration,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['event-registrations'] });
            queryClient.invalidateQueries({ queryKey: ['event-registration', variables.registrationId] });
            // A status change can move a seat, changing the event's registeredCount.
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
};

export const deleteRegistration = async ({ registrationId }: { registrationId: string }) => {
    return callApi({ url: `${eventsAdminApiUrl}/registrations/${registrationId}`, method: 'DELETE' });
};

export const useDeleteRegistration = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteRegistration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['event-registrations'] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
};

/**
 * Download an .xlsx of registrations honouring the current event/status filter (server builds
 * it, all pages). Uses the shared axios instance (auth interceptor) with a blob response, then
 * triggers a browser download. Throws on failure so the caller can surface an error.
 */
export const exportRegistrations = async (params: { status?: EventRegistrationStatus; eventId?: string }): Promise<void> => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.eventId) q.set('eventId', params.eventId);
    const response = await axios({
        url: `${eventsAdminApiUrl}/registrations/export${q.toString() ? `?${q.toString()}` : ''}`,
        method: 'GET',
        responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `event-registrations-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
};

/** Compact "when" label for a stored UTC ISO event start — rendered in the browser's local zone. */
export const formatEventStart = (iso: string): string => {
    try {
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return iso;
    }
};
